import { useEffect, useMemo, useState } from "react";

/* ============================================================================
   SISTEMA-DE-DISENO · buscar y paginar SIN tocar el servidor.

   Varias secciones de Configuración piden la lista COMPLETA a un endpoint que
   no acepta ni búsqueda ni paginación (`listar_clinicas`, `doctors_all`,
   `especialidades_clinica/:id`...). Cambiar esos endpoints es cambiar el
   backend, así que aquí no se toca nada: se filtra y se pagina lo que YA
   llegó, en el navegador.

   Consecuencia honesta, y hay que saberla: el total que se ve es el de lo
   descargado. Si algún día una de esas listas crece hasta no caber en una
   respuesta, la búsqueda tendrá que pasar al servidor -- y eso es un cambio de
   endpoint, que se decide aparte.

   `campos` son las rutas de los valores donde se busca, con puntos para entrar
   en objetos: ["nombre", "ExamenTipo.codigo"]. Se compara sin distinguir
   mayúsculas ni tildes, porque nadie escribe "Cedeño" con la ñ cuando busca
   deprisa.
   ========================================================================== */

const DIACRITICOS = /[̀-ͯ]/g;

const sinTildes = (texto) =>
    String(texto ?? "")
        .normalize("NFD")
        .replace(DIACRITICOS, "")
        .toLowerCase();

const valorDe = (fila, ruta) =>
    ruta.split(".").reduce((actual, parte) => (actual == null ? actual : actual[parte]), fila);

export function filtrarLocal(filas, campos, texto) {
    const buscado = sinTildes(texto).trim();
    if (!buscado) return filas || [];
    return (filas || []).filter((fila) =>
        (campos || []).some((campo) => sinTildes(valorDe(fila, campo)).includes(buscado))
    );
}

export default function useListaLocal(filas, { campos = [], tamanoInicial = 20 } = {}) {
    const [texto, setTexto] = useState("");
    const [pagina, setPagina] = useState(1);
    const [tamano, setTamano] = useState(tamanoInicial);

    // `campos` suele declararse en el render; se compara por contenido para no
    // recalcular en cada pintada por una identidad nueva.
    const clave = campos.join("|");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const filtradas = useMemo(() => filtrarLocal(filas, campos, texto), [filas, clave, texto]);

    // Al buscar se vuelve a la primera página: si no, se busca algo con tres
    // resultados estando en la página 4 y la tabla sale vacía.
    useEffect(() => {
        setPagina(1);
    }, [texto]);

    // Si la lista encoge (se borró una fila, cambió el filtro de arriba), la
    // página actual puede quedarse fuera de rango.
    const paginas = Math.max(1, Math.ceil(filtradas.length / tamano));
    const paginaSegura = Math.min(pagina, paginas);

    const visibles = useMemo(
        () => filtradas.slice((paginaSegura - 1) * tamano, paginaSegura * tamano),
        [filtradas, paginaSegura, tamano]
    );

    return {
        texto,
        setTexto,
        visibles,
        total: filtradas.length,
        hayBusqueda: texto.trim().length > 0,
        limpiar: () => setTexto(""),
        paginacion: {
            page: paginaSegura,
            pageSize: tamano,
            total: filtradas.length,
            tamanos: [20, 50, 100],
            onCambio: (p, ps) => {
                setPagina(p);
                setTamano(ps);
            },
        },
    };
}
